
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/CloudDownloadOutlined";
import Grid2 from "@mui/material/Grid2";
import {
  Box,
  Button,
  Container,
  useMediaQuery,
  CircularProgress,
  useTheme,
  TextField,
  Typography,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  selectState,
  onEmailChanged,
  onLicenseCodeChanged,
  submitAsync,
  productKeyCopiedTapped,
  marketingConsentOnChanged,
  recaptchaOnChanged,
  updateCompanyLogo,
  updateRecaptchaRefresh,
} from "./LandingPageSlice";
import ZoneAlarmLogo from "../../assets/zonealarm_logo_black.webp";
import CourtsMammouthLogo from "../../assets/courts_mammouth_logo.webp";
import CellCityLogo from "../../assets/cellcity_logo.webp";
import ZoneAlarmPromotionalImage from "../../assets/zonealarm_laptop_mobile_shield.webp";
import { ResultCard } from "../../components/result_card/ResultCard";
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';
import { RECAPTCHA_SITE_KEY } from "./recaptcha";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

export function LandingPage() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const theme = useTheme();
  const lessThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(updateCompanyLogo(getSubdomainImage()))
  }, []);



  return (
    <Container maxWidth="sm" disableGutters={lessThanSm}>
      {/* Success state */}
      <Box
        sx={{
          minHeight: "100vh",
          display: state.isSuccessState ? "flex" : "none",
          justifyContent: "center",
          alignItems: "center",
          alignContent: "center",
        }}
      >
        <Grid2
          container
          direction="column"
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <Grid2 sx={{ mt: 3 }}>
            <img height={45} alt={`ZoneAlarm Logo`} src={ZoneAlarmLogo} />
          </Grid2>
          <Grid2 justifyContent="center" alignItems="center">
            <Typography variant="h2" display={"flex"} justifyContent="center" alignItems="center">You deserve the best security</Typography>
            <Typography variant="body1" display={"flex"} justifyContent="center" alignItems="center">
              Welcome to award-winning protection
            </Typography>
          </Grid2>
          <Grid2>
            <img height={180} alt={`ZoneAlarm Promotional Image`} src={ZoneAlarmPromotionalImage} />
          </Grid2>
          <Grid2>
            <Typography sx={{ mb: 1 }} variant="body1">
              Your ZoneAlarm activation code
            </Typography>
            <ResultCard
              value={state.productKey ?? ""}
              smallResult={true}
              description=""
              symbol=""
              resultCopied={state.productKeyCopied}
              onCopy={() => { dispatch(productKeyCopiedTapped()) }} />
          </Grid2>

          <Grid2 sx={{ mt: 1, mb: 3 }} display={"flex"} justifyContent="center" alignItems="center">
            <Button
              component="label"
              variant="contained"
              sx={{ textTransform: "none" }}
              startIcon={<DownloadIcon sx={{ color: "#FFFFFF" }} />}
              onClick={(e: any) => window.open("https://www.zonealarm.com/offers/extreme", "_blank")}
            >
              <Typography variant="h5">Download ZoneAlarm</Typography>
            </Button>
          </Grid2>
        </Grid2>
      </Box>
      {/* Loading State */}
      <Box
        sx={{
          minHeight: "100vh",
          display: state.isLoading && !state.isSuccessState ? "flex" : "none",
          justifyContent: "center",
          alignItems: "center",
          alignContent: "center",
        }}
      >
        <Grid2
          container
          direction="column"
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <Grid2>
            <CircularProgress size={60} />
          </Grid2>
          <Grid2>
            <Typography>Processing</Typography>
          </Grid2>
        </Grid2>
      </Box>
      {/* Form State */}
      <Box
        sx={{
          minHeight: "100vh",
          display: state.isLoading || state.isSuccessState ? "none" : "flex",
          alignItems: "center",
        }}
      >
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12 }}
        >
          <Grid2 size={{ xs: 3, sm: 3, md: 6 }}>
            <img
              width={150}
              alt={`Company Logo`}
              src={state.companyLogo}
            />
          </Grid2>
          <Grid2
            size={{ xs: 3, sm: 3, md: 6 }}
            alignContent={"end"}
            sx={{
              height: { xs: 35, sm: 45, md: 45 },
              display: "flex",
              alignContent: "center",
              justifyContent: "flex-end",
            }}
          >
            <img
              alt={`ZoneAlarm Logo`}
              src={ZoneAlarmLogo} />
          </Grid2>
          <Grid2>
            <Typography>
              Enter your email address and redemption code to redeem your
              license
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <TextField
              fullWidth
              error={state.emailError}
              helperText={state.emailHelperText}
              label="Email"
              variant="outlined"
              type="email"
              color="info"
              value={state.email}
              onChange={(e) => dispatch(onEmailChanged(e.target.value))}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              fullWidth
              error={state.voucherError}
              helperText={state.voucherHelpText}
              label="Code"
              variant="outlined"
              color="info"
              onFocus={(e) => {
                console.log("Update Recaptcha called");
                // dispatch(updateRecaptchaRefresh());
              }}
              value={state.licenseCode}
              onChange={(e) => dispatch(onLicenseCodeChanged(e.target.value))}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue={true}
                name="radio-buttons-group"
                value={state.marketingConsent}
                onChange={(e) => dispatch(marketingConsentOnChanged(e.target.value.toLowerCase() === "true"))}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label={
                    <Typography variant="body2">
                      Send me a discounted renewal offer before my license
                      expires <b style={{ color: "#ED0B5D" }}>(Recommended)</b>
                    </Typography>
                  }
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label={
                    <Typography variant="body2">Only email my key</Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Grid2>
          <Grid2 size={12}>
          </Grid2>
          <Grid2 size={12}>
            <Button
              disabled={state.recaptchaClientToken == ''}
              component="label"
              variant="contained"
              sx={{ textTransform: "none" }}
              startIcon={<SendIcon sx={{ color: "#FFFFFF" }} />}
              onClick={async () => {

                if (!executeRecaptcha) {
                  console.log('Execute recaptcha not yet available');
                  return;
                }

                const token = await executeRecaptcha('submitVoucher');
                if (token == null) return;

                dispatch(submitAsync(token))
              }}
            >
              <Typography variant="h5">Submit</Typography>
            </Button>
          </Grid2>
          <Grid2
            sx={{ mt: 3 }}
            size={12}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="body1">
              Having trouble redeeming your license?
            </Typography>
          </Grid2>
          <Grid2
            sx={{ mt: -2 }}
            size={12}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="body2">
              Contact support via:{" "}
              <a href="mailto:support@voucherprotect.com">support@voucherprotect.com</a>
            </Typography>
          </Grid2>
        </Grid2>
      </Box>
    </Container>
  );
}

const getSubdomainImage = (): string => {
  try {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];

    if (subdomain === 'cellcity') {
      return CellCityLogo;
    } else {
      return CourtsMammouthLogo;
    }
  } catch (error) {
    return CourtsMammouthLogo;
  }
};
